/**
 * @fileoverview Editor de um fluxo de abordagem inteligente (diálogo; tela
 * cheia no celular): nome, conexão, template inicial, quantas mensagens, cada
 * mensagem automática (espera, template e variáveis), horário de envio, janela
 * de atribuição e a prévia da sequência.
 *
 * Reaproveita do editor da triagem o seletor de variáveis (TemplateFields, com
 * as origens deste fluxo), o horário de envio (SendWindowFields), a lista de
 * templates aprovados da conexão e os estilos. As regras do formulário ficam em
 * flowForm.js; o servidor confere tudo de novo ao salvar.
 */

import React, { useMemo, useState } from "react";
import { Dialog, FormControlLabel, MenuItem, Switch, TextField, CircularProgress, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Sparkles, X } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { translateWithLiterals } from "../../utils/translateWithLiterals";
import ModernSelect from "../../components/ModernSelect";
import ConfirmationModal from "../../components/ConfirmationModal";
import UnsavedChangesHint from "../../components/UnsavedChangesHint";
import useManagerStyles from "../Flows/components/TriageManager/styles";
import SendWindowFields from "../Flows/components/TriageManager/SendWindowFields";
import {
  findTemplate,
  templateChoice,
  TemplateFields,
  templateProblemKey,
  useMetaTemplates,
} from "../Flows/components/TriageManager/metaTemplates";
import useStyles from "./styles";
import {
  bodyVariables,
  comparableForm,
  dropMissingInitial,
  emptyForm,
  formFromFlow,
  formProblem,
  MAX_HOURS,
  MAX_MESSAGES,
  MIN_HOURS,
  payloadFromForm,
} from "./flowForm";
import { showSaveError } from "./apiErrors";
import { previewText, templateBodyText } from "../../utils/metaTemplateRules";
import { templateCategoryLabel } from "../../utils/templateCategory";

const t = (key, params) => i18n.t(`smartApproaches.editor.${key}`, params);

const keyOf = (tpl) => `${tpl.name}|${tpl.language}`;

/** Texto com a variável escrita como {{N}} (fora da interpolação do i18next). */
const tVar = (key, params) => translateWithLiterals(`smartApproaches.editor.${key}`, params);

/** Origens das variáveis das mensagens automáticas, com o exemplo da prévia. */
const followUpSources = (initialTemplate) => [
  { value: "contact.firstName", label: t("sources.contactFirstName"), example: "Maria" },
  { value: "contact.name", label: t("sources.contactName"), example: "Maria Souza" },
  { value: "company.name", label: t("sources.companyName"), example: t("sources.companyExample") },
  ...bodyVariables(initialTemplate).map((n) => ({
    value: `initial.${n}`,
    label: tVar("sources.initial", { var: `{{${n}}}` }),
    example: tVar("sources.initialExample", { var: `{{${n}}}` }),
  })),
  { value: "fixed", label: t("sources.fixed") },
];

/** Os problemas do formulário, com a variável escrita como {{N}}. */
const problemText = (key, params) =>
  params && params.variable ? tVar(`problems.${key}`, { ...params, variable: `{{${params.variable}}}` }) : t(`problems.${key}`, params);

const FlowEditor = ({ open, flow, connections, onClose, onSaved }) => {
  const classes = useManagerStyles();
  const own = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  // Montado uma vez: a lista de conexões muda pelo socket (QR, reconexão) e não
  // pode apagar o que está sendo digitado. O editor só existe aberto.
  const [initialForm] = useState(() =>
    flow ? formFromFlow(flow) : emptyForm(connections.length === 1 ? connections[0].id : "")
  );
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const templates = useMetaTemplates(form.whatsappId, open && !!form.whatsappId);
  const initialTemplate = findTemplate(templates.list, form.initial);
  const sources = useMemo(() => followUpSources(initialTemplate), [initialTemplate]);
  const initialVariables = useMemo(() => bodyVariables(initialTemplate), [initialTemplate]);
  const problem = formProblem(form, problemText, initialVariables);
  const dirty = JSON.stringify(comparableForm(form)) !== JSON.stringify(comparableForm(initialForm));
  const templatesReady = !!form.whatsappId && !templates.loading && !templates.error;

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setStep = (index, patch) =>
    setForm((prev) => ({ ...prev, steps: prev.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));

  // Outra conexão, outros templates: as escolhas de template recomeçam.
  const changeConnection = (whatsappId) =>
    setForm((prev) => ({ ...prev, whatsappId, initial: null, steps: prev.steps.map((s) => ({ ...s, template: null })) }));

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const save = async () => {
    if (problem || saving) return;
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      const { data } = flow ? await api.put(`/smart-approaches/${flow.id}`, payload) : await api.post("/smart-approaches", payload);
      toast.success(flow ? t("saved") : t("created"));
      // Fecha o editor (desmonta): nada de estado depois daqui.
      onSaved(data);
      return;
    } catch (err) {
      showSaveError(err);
    }
    setSaving(false);
  };

  const templateWarning = (saved, chosen) =>
    saved && !chosen && templatesReady ? <div className={classes.warnText}>{t("missingTemplate", { name: saved.name })}</div> : null;

  const templateState = templates.loading ? (
    <div className={classes.hint} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <CircularProgress size={14} /> {t("loadingTemplates")}
    </div>
  ) : templates.error ? (
    <div className={classes.warnText}>{t("templatesError")}</div>
  ) : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.paper }}
      >
        <div className={classes.header}>
          <div className={classes.title}>
            <Sparkles size={18} strokeWidth={1.75} />
            <span className={classes.titleSmall}>{flow ? t("titleEdit") : t("titleNew")}</span>
          </div>
          <button type="button" className={classes.iconBtn} onClick={requestClose} aria-label={t("close")}>
            <X />
          </button>
        </div>

        <div className={classes.body}>
          <div className={classes.form}>
            <TextField
              id="smart-approach-name"
              className={classes.field}
              variant="outlined"
              size="small"
              fullWidth
              label={t("name")}
              placeholder={t("namePlaceholder")}
              InputLabelProps={{ shrink: true }}
              value={form.name}
              onChange={(ev) => set({ name: ev.target.value })}
              inputProps={{ maxLength: 120 }}
            />

            <div className={classes.grid2}>
              <ModernSelect
                className={classes.field}
                size="small"
                fullWidth
                label={t("connection")}
                InputLabelProps={{ shrink: true }}
                displayEmpty
                value={form.whatsappId || ""}
                onChange={(ev) => changeConnection(ev.target.value)}
              >
                <MenuItem value="">
                  <em>{t("chooseConnection")}</em>
                </MenuItem>
                {connections.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </ModernSelect>
              <div>
                <div className={classes.label}>{t("count")}</div>
                <div className={classes.dayRow} role="group" aria-label={t("count")}>
                  {Array.from({ length: MAX_MESSAGES }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={form.count === n}
                      className={`${classes.dayBtn} ${form.count === n ? classes.dayBtnOn : ""}`}
                      onClick={() => set({ count: n })}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className={classes.hint} style={{ marginTop: 4 }}>
                  {t("countHint")}
                </div>
              </div>
            </div>

            {form.whatsappId ? (
              <div>
                {templateState || (
                  <ModernSelect
                    className={classes.field}
                    size="small"
                    fullWidth
                    label={t("initial")}
                    helperText={t("initialHint")}
                    InputLabelProps={{ shrink: true }}
                    displayEmpty
                    value={initialTemplate ? keyOf(initialTemplate) : ""}
                    onChange={(ev) => {
                      const tpl = templates.list.find((x) => keyOf(x) === ev.target.value);
                      const available = bodyVariables(tpl);
                      // Valores que a 2ª e a 3ª repetiam de uma {{N}} que o novo template não tem ficam sem valor.
                      setForm((prev) => ({
                        ...prev,
                        initial: tpl ? { name: tpl.name, language: tpl.language } : null,
                        steps: prev.steps.map((s) =>
                          s.template ? { ...s, template: { ...s.template, variables: dropMissingInitial(s.template.variables, available) } } : s
                        ),
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("chooseTemplate")}</em>
                    </MenuItem>
                    {templates.list.map((tpl) => (
                      <MenuItem key={keyOf(tpl)} value={keyOf(tpl)}>
                        {`${tpl.name} (${tpl.language}) · ${templateCategoryLabel(tpl.category)}`}
                      </MenuItem>
                    ))}
                  </ModernSelect>
                )}
                {templateWarning(form.initial, initialTemplate)}
              </div>
            ) : (
              <div className={classes.hint}>{t("connectionFirst")}</div>
            )}

            {form.steps.slice(0, form.count - 1).map((step, i) => {
              const n = i + 2;
              const chosen = findTemplate(templates.list, step.template);
              return (
                <div key={n} className={classes.item} data-step={n}>
                  <div className={classes.itemHead} style={{ cursor: "default" }}>
                    <span className={classes.itemOrder}>{n}</span>
                    <div className={classes.itemTitle}>
                      <div className={classes.itemName}>{t("stepTitle", { n })}</div>
                      <div className={classes.itemSub}>{i === 0 ? t("stepSubFirst") : t("stepSubNext")}</div>
                    </div>
                  </div>
                  <div className={classes.itemBody}>
                    <div className={own.numberRow}>
                      <TextField
                        id={`smart-approach-hours-${n}`}
                        className={classes.field}
                        variant="outlined"
                        size="small"
                        type="number"
                        label={t("hours")}
                        InputLabelProps={{ shrink: true }}
                        value={step.delayHours}
                        onChange={(ev) => setStep(i, { delayHours: ev.target.value })}
                        inputProps={{ min: MIN_HOURS, max: MAX_HOURS, step: 1 }}
                      />
                      <div className={classes.hint}>{t("hoursHint", { min: MIN_HOURS, max: MAX_HOURS })}</div>
                    </div>
                    {form.whatsappId &&
                      (templateState || (
                        <ModernSelect
                          className={classes.field}
                          size="small"
                          fullWidth
                          label={t("template")}
                          helperText={t("templateHint")}
                          InputLabelProps={{ shrink: true }}
                          displayEmpty
                          value={chosen ? keyOf(chosen) : ""}
                          onChange={(ev) => {
                            const tpl = templates.list.find((x) => keyOf(x) === ev.target.value);
                            setStep(i, { template: tpl ? templateChoice(tpl, step.template) : null });
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("chooseTemplate")}</em>
                          </MenuItem>
                          {templates.list.map((tpl) => {
                            const problemKey = templateProblemKey(tpl);
                            const reason = problemKey ? t(`templateProblems.${problemKey}`) : null;
                            return (
                              <MenuItem key={keyOf(tpl)} value={keyOf(tpl)} disabled={!!reason}>
                                {reason ? `${tpl.name} (${tpl.language}) · ${reason}` : `${tpl.name} (${tpl.language})`}
                              </MenuItem>
                            );
                          })}
                        </ModernSelect>
                      ))}
                    {templateWarning(step.template, chosen)}
                    <TemplateFields
                      value={step.template}
                      chosen={chosen}
                      sources={sources}
                      showPreview={false}
                      onChange={(template) => setStep(i, { template })}
                    />
                  </div>
                </div>
              );
            })}

            <SendWindowFields
              title={t("window")}
              hint={t("windowHint")}
              value={form.sendWindow}
              onChange={(patch) => setForm((prev) => ({ ...prev, sendWindow: { ...prev.sendWindow, ...patch } }))}
            />

            <div className={own.numberRow}>
              <TextField
                id="smart-approach-attribution"
                className={classes.field}
                variant="outlined"
                size="small"
                type="number"
                label={t("attribution")}
                InputLabelProps={{ shrink: true }}
                value={form.attributionHours}
                onChange={(ev) => set({ attributionHours: ev.target.value })}
                inputProps={{ min: MIN_HOURS, max: MAX_HOURS, step: 1 }}
              />
              <div className={classes.hint}>{t("attributionHint", { min: MIN_HOURS, max: MAX_HOURS })}</div>
            </div>

            <FormControlLabel
              className={classes.check}
              control={<Switch color="primary" checked={form.active} onChange={(ev) => set({ active: ev.target.checked })} />}
              label={t("activeLabel")}
            />

            <div>
              <div className={classes.label}>{t("preview")}</div>
              <div className={own.timeline}>
                <div className={own.timelineItem}>
                  <div className={own.timelineLabel}>{t("previewFirst")}</div>
                  <div className={classes.tplPreview}>{initialTemplate ? templateBodyText(initialTemplate) : t("previewNoTemplate")}</div>
                </div>
                {form.steps.slice(0, form.count - 1).map((step, i) => {
                  const chosen = findTemplate(templates.list, step.template);
                  return (
                    <div key={i} className={own.timelineItem}>
                      <div className={own.timelineLabel}>
                        {i === 0 ? t("previewAfterFirst", { hours: step.delayHours }) : t("previewAfterNext", { hours: step.delayHours })}
                      </div>
                      <div className={classes.tplPreview}>
                        {chosen ? previewText(chosen, step.template?.variables, sources) : t("previewNoTemplate")}
                      </div>
                    </div>
                  );
                })}
                <div className={own.timelineStop}>{t("previewStop")}</div>
              </div>
            </div>

            <div className={classes.noteBox}>
              <div>{t("costNote")}</div>
              {flow && <div>{t("editNote")}</div>}
            </div>
          </div>
        </div>

        <div className={classes.footer}>
          <div className={classes.footerInfo}>
            {problem ? <span className={classes.footerWarn}>{problem}</span> : <UnsavedChangesHint show={dirty} />}
          </div>
          <div className={classes.footerButtons}>
            <button type="button" className={classes.btnSecondary} onClick={requestClose} disabled={saving}>
              {t("cancel")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={save} disabled={!!problem || saving}>
              {saving ? t("saving") : t("save")}
            </button>
          </div>
        </div>
      </Dialog>

      <ConfirmationModal
        title={t("discardTitle")}
        confirmLabel={t("discard")}
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
      >
        {t("discardText")}
      </ConfirmationModal>
    </>
  );
};

export default FlowEditor;
